import { BreadcrumbJsonLd } from 'next-seo';
import PropTypes from 'prop-types';

import ItemListJsonLd from 'components/Seo/ItemListJsonLd';

import { getOfferImageLink } from 'lib/image';
import { toAbsoluteLink, toOfferLink, toStoreOffersLink } from 'lib/links';
import { toFormattedOfferOldPrice } from 'lib/offer';
import { toFormattedOfferPrice } from 'lib/offerPage';
import {
  formatItemListAsJsonLd,
  formatProductAsJsonLd,
  getCategoryBreadcrumbArr,
} from 'lib/schemas';
import { toFormattedJsonLdString } from 'lib/string';

const CategorySchema = ({
  categoryName,
  categorySlug,
  deepSubcategoryName = null,
  deepSubcategorySlug = null,
  subcategoryName = null,
  subcategorySlug = null,
  offers = [],
}) => (
  <>
    <BreadcrumbJsonLd
      itemListElements={getCategoryBreadcrumbArr({
        categoryName,
        categorySlug,
        subcategoryName,
        subcategorySlug,
        deepSubcategoryName,
        deepSubcategorySlug,
      })}
    />
    <ItemListJsonLd
      description={`Melhores ofertas em ${deepSubcategoryName || subcategoryName || categoryName}`}
      id="category-offers"
      items={offers.slice(0, 10)}
      name={`${deepSubcategoryName || subcategoryName || categoryName} - Promoções`}
      renderItemAsSchema={(
        {
          offerId,
          offerOldPrice,
          offerPhoto,
          offerPrice,
          offerPriceType,
          offerSlug,
          offerTitle,
          storeName,
          storeSlug,
        },
        index
      ) =>
        formatItemListAsJsonLd({
          item: formatProductAsJsonLd({
            images: [getOfferImageLink(offerPhoto, 268)],
            offers: [
              {
                price: offerPrice,
                priceCurrency: 'BRL',
                // TODO: in future we will change it to more dynamic info
                availability: 'InStock',
                url: toAbsoluteLink(toOfferLink(offerSlug)),
                seller: {
                  name: storeName,
                  url: toAbsoluteLink(toStoreOffersLink(storeSlug)),
                },
                offerCount: 1,
                category: categoryName,
                lowPrice: toFormattedOfferPrice(offerPrice, offerPriceType),
                highPrice: toFormattedOfferOldPrice(
                  offerOldPrice,
                  offerPriceType
                ),
              },
            ],
            productName: toFormattedJsonLdString(offerTitle),
            sku: offerId,
          }),
          position: index + 1,
          url: toAbsoluteLink(toOfferLink(offerSlug)),
        })
      }
    />
  </>
);

CategorySchema.propTypes = {
  categoryName: PropTypes.string.isRequired,
  categorySlug: PropTypes.string.isRequired,
  deepSubcategoryName: PropTypes.string,
  deepSubcategorySlug: PropTypes.string,
  subcategoryName: PropTypes.string,
  subcategorySlug: PropTypes.string,
  offers: PropTypes.arrayOf(
    PropTypes.shape({
      categoryId: PropTypes.number,
      categoryName: PropTypes.string,
      categorySlug: PropTypes.string,
      key: PropTypes.string.isRequired,
      offerComments: PropTypes.number.isRequired,
      offerId: PropTypes.number.isRequired,
      offerIsHighlight: PropTypes.bool.isRequired,
      ratings: PropTypes.shape().isRequired,
      offerOldPrice: PropTypes.number,
      offerPhoto: PropTypes.string.isRequired,
      offerPrice: PropTypes.number.isRequired,
      offerPriceType: PropTypes.string.isRequired,
      offerPublished: PropTypes.string.isRequired,
      offerSlug: PropTypes.string.isRequired,
      offerStatusName: PropTypes.string.isRequired,
      offerUserVisibility: PropTypes.string.isRequired,
      offerTags: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          type: PropTypes.string.isRequired,
        })
      ),
      offerTitle: PropTypes.string.isRequired,
      storeDomain: PropTypes.string.isRequired,
      storeId: PropTypes.number,
      subcategoryId: PropTypes.number,
      subcategoryName: PropTypes.string,
      subcategorySlug: PropTypes.string,
    }).isRequired
  ),
};

export default CategorySchema;
